import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Home, LineChart, Loader2, MapPin, Printer } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AuthDialog } from "@/components/site/AuthDialog";
import { EstimadorViviendaWizard } from "@/components/estimador-vivienda/EstimadorViviendaWizard";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/estimador-vivienda")({
  head: () => ({
    meta: [
      { title: "¿Cuánto vale tu vivienda? — Bizaclia" },
      {
        name: "description",
        content:
          "Estima gratis el precio de venta o alquiler de tu piso o casa con anuncios comparables de tu zona. Resultado al momento.",
      },
      { property: "og:title", content: "¿Cuánto vale tu vivienda? — Bizaclia" },
      {
        property: "og:description",
        content:
          "Precio de venta, alquiler y rentabilidad de tu vivienda con anuncios reales de tu zona.",
      },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "/estimador-vivienda" }],
  }),
  component: EstimadorViviendaPage,
});

const PASOS = [
  { icon: MapPin, title: "Indica la dirección", text: "Buscamos anuncios parecidos en tu barrio." },
  {
    icon: Home,
    title: "Describe la vivienda",
    text: "Superficie, habitaciones, planta, ascensor, estado y extras.",
  },
  {
    icon: LineChart,
    title: "Recibe la estimación",
    text: "Precio de venta o alquiler, rango, rentabilidad y los anuncios comparables.",
  },
  { icon: Printer, title: "Guárdala en PDF", text: "Un informe listo para imprimir o compartir." },
];

function EstimadorViviendaPage() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [openAuth, setOpenAuth] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setAuthed(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      setAuthed(!!session);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const comenzar = () => {
    if (authed) setStarted(true);
    else setOpenAuth(true);
  };

  if (started && authed) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-10">
        <header className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">¿Cuánto vale tu vivienda?</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Rellena los datos y calcula el precio con anuncios comparables de tu zona.
          </p>
        </header>
        <EstimadorViviendaWizard />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-4 py-12">
      <header className="max-w-2xl">
        <h1 className="text-4xl font-bold tracking-tight">¿Cuánto vale tu vivienda?</h1>
        <p className="mt-3 text-lg text-muted-foreground">
          Comparamos tu piso o casa con los anuncios activos de tu zona para darte un precio
          orientativo de venta o alquiler, y su rentabilidad, en menos de un minuto.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button size="lg" onClick={comenzar} disabled={authed === null}>
            {authed === null ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando…
              </>
            ) : (
              "Calcular el precio"
            )}
          </Button>
          {authed === false && (
            <span className="text-sm text-muted-foreground">
              Necesitas iniciar sesión para usar el estimador.
            </span>
          )}
        </div>
      </header>

      <section className="mt-12 grid gap-4 sm:grid-cols-2">
        {PASOS.map((p) => (
          <Card key={p.title}>
            <CardContent className="flex gap-4 p-5">
              <span className="mt-0.5 text-primary">
                <p.icon className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-sm font-semibold">{p.title}</span>
                <span className="mt-1 block text-sm text-muted-foreground">{p.text}</span>
              </span>
            </CardContent>
          </Card>
        ))}
      </section>

      <p className="mt-10 max-w-2xl text-xs text-muted-foreground">
        La estimación es orientativa y no constituye una tasación oficial. Se basa en precios de
        oferta de anuncios activos comparables.
      </p>

      <AuthDialog
        open={openAuth}
        onOpenChange={setOpenAuth}
        onComplete={() => {
          setOpenAuth(false);
          setStarted(true);
        }}
      />
    </main>
  );
}
